import { useRef, useState } from 'react';

import type { FileSummary } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';

import { fileUrlFor, useAttachCandidateFile, type AttachmentKind } from '../api';
import { documentProblem } from './AttachmentField';
import styles from '../pages/CandidateDetailPage.module.css';

export function CandidateDocument({
  label,
  kind,
  file,
  candidateId,
  canEdit,
}: {
  label: string;
  kind: AttachmentKind;
  file: FileSummary | null;
  candidateId: string;
  canEdit: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useAttachCandidateFile();
  const toast = useToast();
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [retry, setRetry] = useState<File | null>(null);
  const busy = useRef(false);

  const attach = async (next: File) => {
    if (busy.current || !canEdit) return;
    const invalid = documentProblem(next);
    setProblem(invalid);
    setRetry(null);
    if (invalid) return;
    busy.current = true;
    setProgress(0);
    try {
      await upload(candidateId, kind, next, setProgress);
      toast.success(`${label} uploaded.`);
    } catch (error) {
      setRetry(next);
      setProblem(error instanceof ApiError ? error.message : `${label} could not be uploaded.`);
    } finally {
      busy.current = false;
      setProgress(null);
    }
  };

  return (
    <div>
      <div className={styles.document}>
        <span className={styles.documentLabel}>{label}</span>
        {file ? (
          <a
            className={styles.documentLink}
            href={fileUrlFor(candidateId, file.id)}
            target="_blank"
            rel="noreferrer"
          >
            {file.original_name}
          </a>
        ) : (
          <span className={styles.none}>Not uploaded</span>
        )}
        {canEdit ? (
          <Button
            size="sm"
            variant="subtle"
            loading={progress !== null}
            onClick={() => input.current?.click()}
          >
            {file ? `Replace ${label}` : `Upload ${label}`}
          </Button>
        ) : null}
      </div>
      {progress !== null ? (
        <p role="status">
          Uploading {label}: {Math.round(progress * 100)}%
        </p>
      ) : null}
      {problem ? (
        <Alert tone="danger">
          {problem}
          {retry ? (
            <Button size="sm" variant="ghost" onClick={() => void attach(retry)}>
              Retry upload
            </Button>
          ) : null}
        </Alert>
      ) : null}
      {canEdit ? (
        <input
          ref={input}
          type="file"
          hidden
          aria-label={`Choose ${label}`}
          accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
          onChange={(event) => {
            const next = event.target.files?.[0];
            event.target.value = '';
            if (next) void attach(next);
          }}
        />
      ) : null}
    </div>
  );
}
